import React from "react";
import { AlertTriangle, X } from "lucide-react";

/** Drop-in replacement for window.confirm — works in all browsers including
 *  preview iframes where native dialogs may be blocked or auto-dismissed.
 *
 *  Usage:
 *    const [confirmState, setConfirmState] = useState(null);
 *    setConfirmState({ title, message, onConfirm });
 *    <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
 */
export default function ConfirmDialog({ state, onClose }) {
  if (!state) return null;
  const {
    title = "Are you sure?",
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    danger = false,
    onConfirm,
  } = state;

  const handleConfirm = async () => {
    try {
      if (onConfirm) await onConfirm();
    } finally {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="confirm-dialog-overlay"
    >
      <div
        data-testid="confirm-dialog"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                danger ? "bg-rose-50 text-rose-600" : "bg-orange-50 text-orange-600"
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
            </div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              {title}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {message && <div className="text-sm text-slate-600 mb-5">{message}</div>}

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="confirm-cancel-btn"
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 font-semibold"
          >
            {cancelLabel}
          </button>
          <button
            data-testid="confirm-ok-btn"
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
              danger ? "bg-rose-500 hover:bg-rose-600" : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
